import api from "@/api/api";
import showConfirm from '@/components/utils/ui_v4/alerts/confirm/confirm';
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";
import { API_ENDPOINTS, ERROR_MESSAGES, SUCCESS_MESSAGES } from '@/components/webcotacao/constants';
import alturaDaJanela from "@/helpers/ResponsiveHeightReativo";
import router from '@/router';
import { computed, nextTick, ref, watch } from "vue";
import useCotacaoStore from './useCotacaoStore';

export default function useVisualizarProdutosCotacaoDialog(props) {
  const store = useCotacaoStore();

  // Estado do dialog sincronizado com a prop
  const dialogVisible = ref(false);
  watch(() => props.visible, (newVal) => {
    dialogVisible.value = !!newVal;
  });
  watch(dialogVisible, async (newVal, oldVal) => {
    if (!oldVal && newVal) {
      await carregarProdutos(true);
    }
  });

  const isLoading = ref(false);
  const produtos = ref([]);
  const originalProdutos = ref([]); // <-- Guarda a lista original
  const selected = ref([]);
  const searchQuery = ref("");
  const openPercentTolerancia = ref({ isOpen: false });

  const headers = [
    { text: "EAN/GTIN", value: "ean", tooltip: "Código de barras do produto" },
    { text: "SKU Systock", value: "sku", tooltip: "Código do produto no Systock." },
    { text: "Descrição", value: "descricao", tooltip: "Nome claro do item. Sempre obrigatório."},
    { text: "Preço referência", value: "preco_referencia", tooltip: "Preço do produto cadastrado no Systock para referência. Este preço serve de apoio para você, ele não é enviado ao fornecedor." , align: 'right', width: 140 },
    { text: "Qtd. mín aceitável", value: "min_aceitavel", tooltip: "Limite mínimo que o comprador aceita receber (na U.M. mínima).", align: 'right', width: 140 },
    { text: "Qtd. pedida", value: "qtd_pedida", tooltip: "Quantidade que o comprador <strong>quer</strong> comprar, na U.M. mínima.", align: 'right', width: 120 },
    { text: "Qtd. máx aceitável", value: "max_aceitavel", tooltip: "Limite máximo que o comprador aceita receber (na U.M. mínima).", align: 'right', width: 140 },
    { text: "U.M. mínima", value: "um_minima", tooltip: "Unidade mínima de compra usada como base da cotação (ex.: UN, KG, M).", width: 100 },
    { text: "Qtd. pedido na U.M. master", value: "qtd_pedida_na_um_master", tooltip: "Quantidade pedida equivalente na embalagem master (CX/FARDO etc.).", align: 'right', width: 220 },
    { text: "U.M. master", value: "unidade_master", tooltip: "Embalagem maior (ex.: CX, FARDO, PALLET). Opcional. Ao trocar a Unidade de Medida, defina o <strong>Fator de conversão</strong>.", width: 100 },
    { text: "Fator de conversão (mínima-master)", value: "fator_conversao", tooltip: "Quantas U.M. mínima cabem em 1 U.M. master (ex.: 12, 6, 2.5)." , width: 250 },
    {
      text: "Editar",
      value: "acoes",
      style: {
        backgroundColor: 'white',
        position: 'sticky !important',
        zIndex: '1 !important',
        minWidth: '80px !important',
        maxWidth: '80px !important',
        right: 0,
        left: 'auto',
        borderLeft: '2px solid rgba(0,0,0,.1)',
        cursor: 'pointer'
      }
    },
  ];

  const produtosFiltrados = computed(() => {
    const q = (searchQuery.value || '').toLowerCase();
    if (!q) return produtos.value;
    return (produtos.value || []).filter(p => {
      const ean = String(p.ean || '').toLowerCase();
      const sku = String(p.sku || '').toLowerCase();
      const desc = String(p.descricao || '').toLowerCase();
      return ean.includes(q) || sku.includes(q) || desc.includes(q);
    });
  });

  const alturaTabela = computed(() => alturaDaJanela.value - 220);
  const isAllSelected = computed(() => selected.value.length <= 0);

  async function carregarProdutos(reset) {
    if (!props.idCotacao) return;
    try {
      isLoading.value = true;
      const { data: response } = await api.get(`/cotacao/listar_produtos_cotacao/${props.idCotacao}`);
      const lista = response && response.produtos ? response.produtos : [];

      const norm = lista.map((p, idx) => ({
        _itemId: p._itemId || p.id || `${idx}-${(p.idproduto || p.sku || p.ean)}`,
        cotacao_produtos_id: p.cotacao_produtos_id || p.id,
        ean: p.codigo_barras || '',
        sku: p.idproduto || '',
        descricao: p.descricao_produto || '',
        min_aceitavel: p.qtd_minima_aceita_emb || '',
        qtd_pedida: p.qtd_pedida || 0,
        max_aceitavel: p.qtd_maxima_aceita_emb || '',
        qtd_pedida_na_um_master: p.multiplo_compra || 0,
        um_minima: p.unidade_master || '',
        preco_referencia: p.preco_custo || 0,
        codigo_barras: p.codigo_barras || '0',
        idproduto: p.idproduto || '',
        descricao_produto: p.descricao_produto || '',
        multiplo_compra: p.multiplo_compra || 0,
        unidade_master: p.unidade_master || '',
        idunidade_medida: p.idunidade_medida || null,
        fator_conversao: p.fator_conversao || '1.000000',
        cod_produto: p.cod_produto || '',
        preco_custo: p.preco_custo || 0,
      }));

      produtos.value = JSON.parse(JSON.stringify(norm));
      originalProdutos.value = JSON.parse(JSON.stringify(norm));

      if (reset) selected.value = [];
      await nextTick();
    } catch (e) {
      void e;
    } finally {
      isLoading.value = false;
    }
  }

  function handleResetProdutos() {
    produtos.value = JSON.parse(JSON.stringify(originalProdutos.value));
    toast.info("A lista de produtos foi restaurada.");
  }

  const limitDecimals = (value, decimals = 6) => {
    if (value === null || value === undefined || value === '') return null;
    let num = Number(value);
    if (isNaN(num)) return 0;

    // Limite do banco de dados (precision 12, scale 4 implies max < 10^8)
    const MAX_DB_VAL = 99999999;
    if (num > MAX_DB_VAL) num = MAX_DB_VAL;

    // Se for inteiro, retorna inteiro
    if (Number.isInteger(num)) return num;

    // Arredonda para o número de casas decimais especificado
    return parseFloat(num.toFixed(decimals));
  };

  async function salvarProdutos() {
    if (!props.idCotacao) return;

    try {
      isLoading.value = true;

      const payload = {
        produtos: produtos.value.map(p => ({
          cotacao_produtos_id: p.cotacao_produtos_id,
          codigo_barras: p.codigo_barras || '0',
          idproduto: p.idproduto || p.sku,
          descricao_produto: p.descricao_produto || p.descricao,
          qtd_minima_aceita_emb: limitDecimals(p.min_aceitavel),
          qtd_pedida: String(limitDecimals(p.qtd_pedida) || 0),
          qtd_maxima_aceita_emb: limitDecimals(p.max_aceitavel),
          idunidade_medida: p.idunidade_medida,
          multiplo_compra: String(limitDecimals(p.qtd_pedida_na_um_master) || 0),
          unidade_master: p.unidade_master || p.um_minima,
          fator_conversao: p.fator_conversao || '1.000000',
          cod_produto: p.cod_produto || '',
          preco_custo: String(Number(p.preco_custo) || Number(p.preco_referencia) || 0),
        }))
      };

      await api.put(`/cotacao/atualizar_produtos_cotacao/${props.idCotacao}`, payload);
      toast.success('Produtos atualizados com sucesso!');
    } catch (e) {
      
      toast.error('Erro ao salvar produtos. Tente novamente.');
    } finally {
      isLoading.value = false;
    }
  }

  async function handleFecharDialog() {
    if (produtos.value.length === 0) {
      showConfirm({
        title: 'Cotação sem produtos',
        message: 'A cotação não possui produtos e será excluída se você continuar. Deseja confirmar a exclusão?',
        button: {
          yes: 'Sim, excluir cotação',
          no: 'Cancelar'
        },
        callback: async (confirmed) => {
          if (confirmed) {
            try {
              isLoading.value = true;
              await api.delete(`${API_ENDPOINTS.DELETAR_COTACAO}/${props.idCotacao}`);
              toast.success(SUCCESS_MESSAGES.DELETE_COTACAO);

              // Atualizar store
              store.removeCotacaoLocal(props.idCotacao);
              await store.refresh();

              if (props.handleClose) {
                props.handleClose();
              }

              await router.push('/dashboard');

            } catch (error) {
              const message = (error && error.response && error.response.data && error.response.data.message) || ERROR_MESSAGES.DELETE_COTACAO;
              if (!error.response && error.name === 'NavigationDuplicated') {
                // ignorar
              } else if (error.response) {
                toast.error(message);
              } else {
                try {
                  await router.push('/');
                } catch (e) {
                  void e;
                }
              }
            } finally {
              isLoading.value = false;
            }
          }
        }
      });
      return;
    }

    await salvarProdutos();
    if (props.handleClose) {
      props.handleClose();
    }
  }

  function handleRemover(item) {
    produtos.value = (produtos.value || []).filter(p => p._itemId !== item._itemId);
    selected.value = (selected.value || []).filter(id => id !== item._itemId && id !== item);
  }

  function handleExcluir(item) {
    handleRemover(item);
  }

  function handleExcluirSelecionados() {
    if (!selected.value || selected.value.length === 0) return;

    showConfirm({
      title: 'Remover produtos selecionados',
      message: `Tem certeza que deseja remover ${selected.value.length} produto(s) selecionado(s)?`,
      button: {
        yes: 'Confirmar',
        no: 'Cancelar'
      },
      callback: (confirmed) => {
        if (confirmed) {
          const ids = new Set(selected.value.map(s => (typeof s === 'object' ? s._itemId : s)));
          produtos.value = (produtos.value || []).filter(p => !ids.has(p._itemId));
          selected.value = [];
          toast.success('Itens selecionados removidos com sucesso!');
        }
      }
    });
  }

  function handleClear() {
    showConfirm({
      title: 'Limpar lista',
      message: 'Tem certeza que deseja limpar a lista e remover todos os produtos?',
      button: {
        yes: 'Limpar',
        no: 'Cancelar'
      },
      callback: (confirmed) => {
        if (confirmed) {
          produtos.value = [];
          selected.value = [];
          toast.success('Todos os itens da lista foram excluídos.');
        }
      }
    });
  }

  const formatBRL = (valor) => {
    if (valor == null) return ''
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(valor)
  }

  function handlePercentTolerancia() {
    openPercentTolerancia.value.isOpen = true;
  }

  const loadTolerancia = async (data) => {
    const { minima, maxima, applyAll } = data;
    openPercentTolerancia.value.isOpen = false;

    // Garantir que os percentuais sejam números
    const minPercent = Number(minima) || 0;
    const maxPercent = Number(maxima) || 0;

    const selectedKeys = new Set((selected.value || []).map(item => (typeof item === 'object' ? item._itemId : item)));

    produtos.value = (produtos.value || []).map(item => {
      if (!applyAll && !selectedKeys.has(item._itemId)) {
        return item;
      }

      const qtd = Number(item.qtd_pedida) || 0;

      // Se quantidade pedida for 0, min/max = 0
      if (qtd === 0) {
        return { ...item, min_aceitavel: 0, max_aceitavel: 0 };
      }

      // Se ambos percentuais forem 0, min/max = 0
      if (minPercent === 0 && maxPercent === 0) {
        return { ...item, min_aceitavel: 0, max_aceitavel: 0 };
      }

      // Calcular valores mínimos e máximos
      // Se percentual for 0, o valor correspondente será 0
      const minCalc = minPercent === 0 ? 0 : Math.max(0, qtd - Math.floor((qtd * minPercent) / 100));
      const maxCalc = maxPercent === 0 ? 0 : qtd + Math.ceil((qtd * maxPercent) / 100);

      // Garantir coerência min <= max
      const minFinal = Math.max(0, Math.min(minCalc, maxCalc));
      const maxFinal = Math.max(minFinal, maxCalc);

      return {
        ...item,
        min_aceitavel: minFinal,
        max_aceitavel: maxFinal,
      };
    });

    const count = applyAll ? (produtos.value ? produtos.value.length : 0) : selectedKeys.size;

    if (minPercent === 0 && maxPercent === 0) {
      const msg = applyAll
        ? `Tolerância zerada para ${count} itens da cotação.`
        : `Tolerância zerada para ${count} itens selecionados.`;
      toast.success(msg);
    } else {
      const msg = applyAll
        ? `Tolerância aplicada para ${count} itens da cotação.`
        : `Tolerância aplicada para ${count} itens selecionados.`;
      toast.success(msg);
    }
  };

  return {
    dialogVisible,
    isLoading,
    produtos,
    selected,
    searchQuery,
    openPercentTolerancia,
    headers,
    produtosFiltrados,
    alturaTabela,
    isAllSelected,
    handleFecharDialog,
    handleExcluir,
    handleExcluirSelecionados,
    handleClear,
    formatBRL,
    handlePercentTolerancia,
    loadTolerancia,
    handleResetProdutos,
  };
}
